<app-header-toolbar
  [userDisplayName]="getUserDisplayName()"
  (logout)="logout()">
</app-header-toolbar>

<div class="page">
  <!-- Loading -->
  <div class="state-block" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading account…</p>
  </div>

  <!-- Account detail -->
  <main class="wrap" *ngIf="account && !loading">

    <!-- ============================ Status header ============================ -->
    <div class="acct-hd">
      <div class="acct-hd-main">
        <p class="eyebrow">
          Account detail<span *ngIf="account.programName"> · {{ account.programName }}</span>
        </p>
        <h1 class="hero-title">
          <span class="id-num">A-{{ account.quoteId.toString().padStart(6, '0') }}</span>
          <span class="badge" [ngClass]="getStatusBadgeClass(account.status)">
            {{ getStatusDisplayName(account.status) }}
          </span>
        </h1>
        <p class="acct-insured">{{ account.insuredName }}</p>
      </div>

      <div class="acct-actions">
        <button type="button" class="btn-ghost" (click)="goBack()">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          Back to console
        </button>
      </div>
    </div>

    <!-- ===================== Balance / next-payment callout ==================== -->
    <section class="hero-band" aria-label="Account balance">
      <div class="hero-band-cols">
        <div class="hero-band-primary">
          <div class="hero-band-l mono">Balance remaining</div>
          <div class="hero-band-big">{{ formatWhole(account.balanceRemaining) }}</div>
          <p class="hero-band-line">
            <span class="mono">{{ formatWhole(account.amountPaid) }}</span> paid
            · <span class="mono">{{ formatWhole(account.financing.totalOfPayments) }}</span> total of payments
          </p>
        </div>

        <div class="next-due" *ngIf="account.nextDueDate">
          <div class="next-due-l mono">Next payment</div>
          <div class="next-due-amt mono">{{ formatCurrency(account.nextAmount) }}</div>
          <div class="next-due-date">
            due <span class="mono">{{ formatDate(account.nextDueDate) }}</span>
          </div>
        </div>
      </div>

      <!-- delinquency / grace notice -->
      <div class="acct-flag flag--delinquent" *ngIf="isDelinquent">
        <mat-icon aria-hidden="true">error</mat-icon>
        <span>Delinquent since <b class="mono">{{ formatDate(account.delinquentStartedAt) }}</b></span>
      </div>
      <div class="acct-flag flag--grace" *ngIf="isGrace">
        <mat-icon aria-hidden="true">schedule</mat-icon>
        <span>In grace period since <b class="mono">{{ formatDate(account.graceStartedAt) }}</b></span>
      </div>
    </section>

    <!-- ========================= Financing summary ========================= -->
    <div class="fin">
      <div class="fin-cap mono">
        <mat-icon aria-hidden="true">account_balance</mat-icon>
        Financing summary
      </div>
      <div class="fin-grid">
        <div class="fin-cell major">
          <div class="fin-lab">Amount financed</div>
          <div class="fin-val mono">{{ formatCurrency(account.financing.amountFinanced) }}</div>
        </div>
        <div class="fin-cell major">
          <div class="fin-lab">Down payment</div>
          <div class="fin-val mono">{{ formatCurrency(account.financing.downPayment) }}</div>
        </div>
        <div class="fin-cell major">
          <div class="fin-lab">Monthly payment</div>
          <div class="fin-val mono">{{ formatCurrency(account.financing.monthlyPayment) }}<span class="unit">/mo</span></div>
        </div>
        <div class="fin-cell minor">
          <div class="fin-lab">Rate (APR)</div>
          <div class="fin-val mono">{{ account.financing.rate }}<span class="unit">%</span></div>
        </div>
        <div class="fin-cell minor">
          <div class="fin-lab">Term</div>
          <div class="fin-val mono">{{ account.financing.term }}<span class="unit">mo</span></div>
        </div>
        <div class="fin-cell minor">
          <div class="fin-lab">Total of payments</div>
          <div class="fin-val mono">{{ formatCurrency(account.financing.totalOfPayments) }}</div>
        </div>
      </div>
    </div>

    <!-- ===================== Fees (KAN-6) ==================== -->
    <div class="card" *ngIf="hasFees">
      <div class="card-head">
        <h2>Fees</h2>
        <span class="card-badge mono">{{ formatCurrency(outstandingFees) }} outstanding</span>
      </div>
      <div class="card-body">
        <div class="table-wrap" style="overflow-x:auto">
          <table style="width:100%;border-collapse:collapse;font-size:14px">
            <thead>
              <tr style="text-align:left;color:var(--ifq-muted);font-size:11px;text-transform:uppercase;letter-spacing:.06em">
                <th style="padding:6px 10px 10px 0">Fee</th>
                <th style="padding:6px 10px 10px 0">Amount</th>
                <th style="padding:6px 10px 10px 0">Status</th>
                <th style="padding:6px 0 10px 0"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let fee of fees" style="border-top:1px solid var(--ifq-line)">
                <td style="padding:10px 10px 10px 0">{{ fee.type }}</td>
                <td class="mono" style="padding:10px 10px 10px 0">{{ formatCurrency(fee.amount) }}</td>
                <td style="padding:10px 10px 10px 0">
                  <span class="badge" [ngClass]="fee.isWaived || isFeePaid(fee) ? 'badge--muted' : 'badge--warn'">{{ fee.collectionStatus }}</span>
                  <span *ngIf="fee.isWaived && fee.waiveReason" style="display:block;color:var(--ifq-muted);font-size:12px;margin-top:3px">{{ fee.waiveReason }}</span>
                </td>
                <td style="padding:10px 0;text-align:right">
                  <button
                    *ngIf="!fee.isWaived && !isFeePaid(fee) && isSuperAdmin"
                    type="button"
                    mat-stroked-button
                    (click)="waiveFee(fee)"
                    [disabled]="waivingFeeId === fee.id">
                    <mat-icon aria-hidden="true">gavel</mat-icon>
                    {{ waivingFeeId === fee.id ? 'Waiving…' : 'Waive' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p *ngIf="!isSuperAdmin" style="margin:12px 0 0;color:var(--ifq-muted);font-size:12px">Only a super-admin (CEO / finance admin) can waive fees.</p>
      </div>
    </div>

    <!-- ===================== Manage account (servicing hub) ==================== -->
    <!--
      KAN-38 · Servicing hub. The three actions in the first group hit a real,
      producer-authenticated endpoint. Everything under "Coming soon" has no
      producer endpoint yet, so it renders as a clearly-labelled disabled control
      rather than a broken action. Money terms stay read-only everywhere.
    -->
    <div class="card manage-card">
      <div class="card-head">
        <mat-icon aria-hidden="true">tune</mat-icon>
        <h2>Manage account</h2>
        <span class="card-badge">Servicing · terms locked</span>
      </div>
      <div class="card-body">
        <p class="manage-note">
          Financing terms are locked once an account is active. Use these
          servicing actions to help the customer manage payments and paperwork.
        </p>

        <!-- KAN-5 · agreement signature status -->
        <div class="sig-status" [class.signed]="account.agreementSignedAt">
          <mat-icon aria-hidden="true">{{ account.agreementSignedAt ? 'verified' : 'pending' }}</mat-icon>
          <div class="sig-status-txt">
            <div class="sig-status-t">
              <ng-container *ngIf="account.agreementSignedAt">
                Customer signed · <span class="mono">{{ formatDate(account.agreementSignedAt) }}</span>
              </ng-container>
              <ng-container *ngIf="!account.agreementSignedAt">Awaiting customer signature</ng-container>
            </div>
            <div class="sig-status-d" *ngIf="account.agreementCompletedAt">
              Fully executed · <span class="mono">{{ formatDate(account.agreementCompletedAt) }}</span>
            </div>
            <div class="sig-status-d" *ngIf="account.agreementSignedAt && !account.agreementCompletedAt">
              Awaiting agent signature
            </div>
          </div>
          <button
            *ngIf="awaitingAgentSignature"
            type="button"
            mat-flat-button
            color="primary"
            class="sig-sign-btn"
            (click)="signAsAgent()"
            [disabled]="agentSigning || agentSignReturning">
            <mat-spinner *ngIf="agentSigning || agentSignReturning" diameter="18"></mat-spinner>
            {{ agentSigning ? 'Opening…' : (agentSignReturning ? 'Confirming…' : 'Sign as agent') }}
          </button>
        </div>

        <!-- Wired producer actions -->
        <div class="manage-grid">
          <button type="button" class="svc-action" (click)="emailAccountLink()">
            <span class="svc-ic"><mat-icon aria-hidden="true">mail</mat-icon></span>
            <span class="svc-txt">
              <span class="svc-t">Email account link</span>
              <span class="svc-d">Send the secure link so the customer can view their account and update their payment method.</span>
            </span>
          </button>

          <button type="button" class="svc-action" (click)="copyAccountLink()" [disabled]="copyingLink">
            <span class="svc-ic"><mat-icon aria-hidden="true">link</mat-icon></span>
            <span class="svc-txt">
              <span class="svc-t">Copy account link</span>
              <span class="svc-d">Copy the customer's secure account link to your clipboard.</span>
            </span>
          </button>

          <button type="button" class="svc-action" (click)="downloadAgreement()" [disabled]="downloadingPdf">
            <span class="svc-ic"><mat-icon aria-hidden="true">description</mat-icon></span>
            <span class="svc-txt">
              <span class="svc-t">Download finance agreement</span>
              <span class="svc-d">Download the signed Premium Finance Agreement (PDF).</span>
            </span>
          </button>
        </div>

        <!--
          KAN-41 · Real, producer-authenticated servicing actions. These move or
          settle money against the locked balance (they never re-price terms):
          each opens a confirm/form dialog, calls its endpoint, then reloads.
        -->
        <div class="manage-cap mono">
          <mat-icon aria-hidden="true">payments</mat-icon>
          Servicing actions
        </div>
        <div class="manage-grid">
          <button type="button" class="svc-action svc-action--pay" (click)="payOffAccount()" [disabled]="!isServiceable || payoffLoading">
            <span class="svc-ic">
              <mat-spinner *ngIf="payoffLoading" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!payoffLoading" aria-hidden="true">price_check</mat-icon>
            </span>
            <span class="svc-txt">
              <span class="svc-t">Pay off account</span>
              <span class="svc-d">Fetch the current payoff quote and settle the remaining balance in full.</span>
            </span>
          </button>

          <button type="button" class="svc-action" (click)="recordManualPayment()" [disabled]="!isServiceable || recordingPayment">
            <span class="svc-ic">
              <mat-spinner *ngIf="recordingPayment" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!recordingPayment" aria-hidden="true">point_of_sale</mat-icon>
            </span>
            <span class="svc-txt">
              <span class="svc-t">Record manual payment</span>
              <span class="svc-d">Log a payment collected outside of Stripe (check, wire, or cash).</span>
            </span>
          </button>

          <button type="button" class="svc-action svc-action--danger" (click)="cancelAccount()" [disabled]="!isServiceable || cancelLoading">
            <span class="svc-ic">
              <mat-spinner *ngIf="cancelLoading" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!cancelLoading" aria-hidden="true">cancel</mat-icon>
            </span>
            <span class="svc-txt">
              <span class="svc-t">Cancel account</span>
              <span class="svc-d">Stop future billing and mark the account Cancelled; shows the estimated unearned refund.</span>
            </span>
          </button>
        </div>

        <!-- Coming-soon servicing operations (no producer endpoint yet) -->
        <div class="manage-soon">
          <div class="manage-soon-cap mono">
            <mat-icon aria-hidden="true">schedule</mat-icon>
            Coming soon
          </div>
          <div class="manage-grid manage-grid--soon">
            <button type="button" class="svc-action svc-action--soon" disabled aria-disabled="true"
                    matTooltip="Not available yet — customers update payment methods from the secure account link.">
              <span class="svc-ic"><mat-icon aria-hidden="true">credit_card</mat-icon></span>
              <span class="svc-txt">
                <span class="svc-t">Change payment method <span class="soon-chip">Soon</span></span>
                <span class="svc-d">Update the card or bank account on file from the console.</span>
              </span>
            </button>

            <button type="button" class="svc-action svc-action--soon" disabled aria-disabled="true"
                    matTooltip="Not available yet">
              <span class="svc-ic"><mat-icon aria-hidden="true">undo</mat-icon></span>
              <span class="svc-txt">
                <span class="svc-t">Refund payment <span class="soon-chip">Soon</span></span>
                <span class="svc-d">Refund a processed Stripe payment.</span>
              </span>
            </button>

            <button type="button" class="svc-action svc-action--soon" disabled aria-disabled="true"
                    matTooltip="Not available yet">
              <span class="svc-ic"><mat-icon aria-hidden="true">restart_alt</mat-icon></span>
              <span class="svc-txt">
                <span class="svc-t">Reinstate account <span class="soon-chip">Soon</span></span>
                <span class="svc-d">Restore a cancelled or lapsed account.</span>
              </span>
            </button>

            <button type="button" class="svc-action svc-action--soon" disabled aria-disabled="true"
                    matTooltip="Not available yet — terms are locked once accepted.">
              <span class="svc-ic"><mat-icon aria-hidden="true">edit_calendar</mat-icon></span>
              <span class="svc-txt">
                <span class="svc-t">Restructure terms <span class="soon-chip">Soon</span></span>
                <span class="svc-d">Adjust the schedule or rate on an existing account.</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== Flight-path progress (signature) ==================== -->
    <section class="sig" aria-label="Repayment flight path">
      <h2>Repayment flight path</h2>
      <p class="sig-hint">The down payment is the takeoff; the aircraft climbs toward paid-in-full as installments clear.</p>
      <div class="path">
        <svg class="path-line" viewBox="0 0 600 128" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="flightGradAcct" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" class="grad-start"/>
              <stop offset="1" class="grad-end"/>
            </linearGradient>
          </defs>
          <line x1="30" y1="102" x2="570" y2="29" stroke="url(#flightGradAcct)" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <div
          *ngFor="let n of getPathNodes()"
          class="mk"
          [class.takeoff]="n.type === 'takeoff'"
          [class.now]="n.type === 'now'"
          [class.end]="n.type === 'end'"
          [style.left.%]="n.left"
          [style.bottom.%]="n.bottom">
          <span class="mk-lab mono" *ngIf="n.label">{{ n.label }}</span>
        </div>
      </div>
      <div class="ends">
        <span><b class="mono">{{ formatWhole(account.financing.downPayment) }}</b> down</span>
        <span class="mid mono">{{ formatWhole(account.amountPaid) }} paid</span>
        <span><b class="mono">{{ formatWhole(account.balanceRemaining) }}</b> remaining</span>
      </div>
    </section>

    <!-- ========================= Insured / contact ========================= -->
    <div class="card">
      <div class="card-head">
        <mat-icon aria-hidden="true">person</mat-icon>
        <h2>Insured &amp; contact</h2>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <div class="info-k">Insured name</div>
            <div class="info-v">{{ account.insuredName }}</div>
          </div>
          <div class="info-item" *ngIf="account.agentName">
            <div class="info-k">Agent / aircraft</div>
            <div class="info-v">{{ account.agentName }}</div>
          </div>
          <div class="info-item" *ngIf="account.email">
            <div class="info-k">Email</div>
            <div class="info-v">{{ account.email }}</div>
          </div>
          <div class="info-item" *ngIf="account.phone">
            <div class="info-k">Phone</div>
            <div class="info-v mono">{{ account.phone }}</div>
          </div>
          <div class="info-item" *ngIf="account.mobile">
            <div class="info-k">Mobile</div>
            <div class="info-v mono">{{ account.mobile }}</div>
          </div>
          <div class="info-item" *ngIf="account.customerNumber">
            <div class="info-k">Customer number</div>
            <div class="info-v mono">{{ account.customerNumber }}</div>
          </div>
          <div class="info-item" *ngIf="account.addressLine1">
            <div class="info-k">Address</div>
            <div class="info-v">{{ account.addressLine1 }}<span *ngIf="account.addressLine2">, {{ account.addressLine2 }}</span><span *ngIf="account.city">, {{ account.city }}</span><span *ngIf="account.state">, {{ account.state }}</span><span *ngIf="account.postalCode"> {{ account.postalCode }}</span></div>
          </div>
          <div class="info-item">
            <div class="info-k">Program</div>
            <div class="info-v">{{ account.programName || 'N/A' }}</div>
          </div>
          <div class="info-item">
            <div class="info-k">Opened</div>
            <div class="info-v mono">{{ formatDate(account.createdAt) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================ Policies ============================ -->
    <div class="card" *ngIf="hasPolicies">
      <div class="card-head">
        <mat-icon aria-hidden="true">policy</mat-icon>
        <h2>Policies</h2>
        <span class="card-badge">{{ policies.length }} {{ policies.length === 1 ? 'policy' : 'policies' }} · terms locked</span>
      </div>
      <div class="card-body">
        <div class="policy-list">
          <div class="policy-item" *ngFor="let policy of policies">
            <div class="policy-head">
              <div class="policy-no">
                <span class="plabel">Policy</span>
                <span class="mono">#{{ policy.policyNumber }}</span>
              </div>
              <div class="policy-prem mono">{{ formatCurrency(policy.totalPremium) }}</div>
            </div>
            <div class="policy-grid">
              <div class="policy-detail">
                <div class="pd-k">Coverage</div>
                <div class="pd-v">{{ getCoverageDisplayName(policy.coverage) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Effective date</div>
                <div class="pd-v mono">{{ formatDate(policy.effectiveDate) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Expiration date</div>
                <div class="pd-v mono">{{ formatDate(policy.expirationDate) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Premium</div>
                <div class="pd-v mono">{{ formatCurrency(policy.premiumAmount) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Taxes</div>
                <div class="pd-v mono">{{ formatCurrency(policy.taxesAmount) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Fees</div>
                <div class="pd-v mono">{{ formatCurrency(policy.feesAmount) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Down payment</div>
                <div class="pd-v mono">{{ formatCurrency(policy.downAmount) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================= Payment schedule ========================= -->
    <div class="card">
      <div class="card-head">
        <mat-icon aria-hidden="true">event_repeat</mat-icon>
        <h2>Payment schedule</h2>
        <span class="card-badge" *ngIf="hasSchedule">{{ account.schedule.installments.length }} installments</span>
      </div>
      <div class="card-body">
        <div class="ledger" *ngIf="hasSchedule">
          <table class="ledger-table">
            <thead>
              <tr>
                <th class="col-idx">#</th>
                <th class="col-date">Due date</th>
                <th class="col-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let it of account.schedule.installments; trackBy: trackByInstallment">
                <td class="col-idx mono">{{ it.installment }}</td>
                <td class="col-date mono">{{ formatDate(it.dueDate) }}</td>
                <td class="col-num mono">{{ formatCurrency(it.amount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" class="foot-l">Total of payments</td>
                <td class="col-num mono">{{ formatCurrency(account.financing.totalOfPayments) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="empty-inline" *ngIf="!hasSchedule">
          <mat-icon aria-hidden="true">info</mat-icon>
          <p>No amortization schedule is available for this account.</p>
        </div>
      </div>
    </div>

    <!-- ========================= Payment history ========================= -->
    <div class="card">
      <div class="card-head">
        <mat-icon aria-hidden="true">payments</mat-icon>
        <h2>Payment history</h2>
        <span class="card-badge" *ngIf="hasPayments">{{ account.payments.length }} recorded</span>
      </div>
      <div class="card-body">
        <ul class="pay-history" *ngIf="hasPayments">
          <li class="pay-row" *ngFor="let p of account.payments; trackBy: trackByPayment">
            <div class="pay-icon">
              <mat-icon aria-hidden="true">north_east</mat-icon>
            </div>
            <div class="pay-body">
              <div class="pay-type">{{ getPaymentTypeDisplayName(p.paymentType) }}</div>
              <div class="pay-date mono">{{ formatDate(p.paymentDate) }}</div>
            </div>
            <span class="badge" [ngClass]="getPaymentStatusClass(p.status)">{{ p.status }}</span>
            <div class="pay-amt mono">{{ formatCurrency(p.amount) }}</div>
          </li>
        </ul>

        <div class="empty-inline" *ngIf="!hasPayments">
          <mat-icon aria-hidden="true">info</mat-icon>
          <p>No payments have been recorded on this account yet.</p>
        </div>
      </div>
    </div>

  </main>
</div>
